/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useState } from 'react';
import {
    CircleStackIcon,
    ArrowUpTrayIcon,
    TrashIcon,
    FolderIcon,
    DocumentIcon,
    ArrowDownTrayIcon,
    Cog6ToothIcon,
    FolderOpenIcon,
    ClipboardDocumentIcon,
    LinkIcon,
    GlobeAltIcon,
    CurrencyRupeeIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { Bucket, ObjectInfo } from '../../types';
import { formatBytes } from '../../utils/formateSize';
import BucketSettingsPanel from './BucketSettingsPanel';
import DeleteObjectModal from './DeleteObjectModal';
import { UpdateBucketData } from '../../hooks/useBuckets';

// Format currency in INR
const formatCurrency = (amount: number): string => {
    if (amount === 0) return '₹0.00';
    if (amount < 1) return `₹${amount.toFixed(4)}`;
    return `₹${amount.toFixed(2)}`;
};

type TabType = 'objects' | 'settings';

interface BucketDetailsProps {
    bucket: Bucket;
    projectId: string;
    objects: ObjectInfo[];
    currentPath: string;
    isLoading: boolean;
    onBack: () => void;
    onUpload: () => void;
    onDeleteBucket: () => void;
    onNavigateFolder: (folder: string) => void;
    onNavigateUp: () => void;
    onDownload: (obj: ObjectInfo) => void;
    onDeleteObject: (key: string) => void;
    onUpdateBucket: (data: UpdateBucketData) => Promise<void>;
    isUpdating: boolean;
    updateError?: string | null;
}

const BucketDetails: React.FC<BucketDetailsProps> = ({
    bucket,
    projectId,
    objects,
    currentPath,
    isLoading,
    onBack,
    onUpload,
    onDeleteBucket,
    onNavigateFolder,
    onNavigateUp,
    onDownload,
    onDeleteObject,
    onUpdateBucket,
    isUpdating,
    updateError,
}) => {
    const [activeTab, setActiveTab] = useState<TabType>('objects');
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [objectToDelete, setObjectToDelete] = useState<{ key: string; name: string; isFolder: boolean } | null>(null);

    // Generate public URL base - handles different environments
    const getPublicBaseUrl = () => {
        // Try to derive the cloud URL from current origin
        const origin = window.location.origin;
        // If on portal subdomain, replace with cloud
        if (origin.includes('portal.')) {
            return origin.replace('portal.', 'cloud.');
        }
        // If on localhost or other, use cloud.podstack.ai
        if (origin.includes('localhost') || origin.includes('127.0.0.1')) {
            return 'https://cloud.podstack.ai';
        }
        // Default: assume same domain
        return origin;
    };

    // Generate public URL for the bucket (list objects)
    const getPublicBucketUrl = () => {
        return `${getPublicBaseUrl()}/platform/api/v1/public/${bucket.name}`;
    };

    // Generate public URL for downloading an object (S3-style path)
    const getPublicObjectUrl = (objectKey: string) => {
        return `${getPublicBaseUrl()}/platform/api/v1/public/${bucket.name}/${objectKey}`;
    };

    // Copy URL to clipboard
    const copyToClipboard = (url: string, label: string) => {
        navigator.clipboard.writeText(url).then(() => {
            toast.success(`${label} copied to clipboard`);
        }).catch(() => {
            toast.error('Failed to copy to clipboard');
        });
    };

    // Calculate total size from objects array for real-time updates
    const totalSizeBytes = objects.reduce((sum, obj) => {
        const isFolder = obj.is_dir || obj.is_folder;
        return sum + (isFolder ? 0 : (obj.size || 0));
    }, 0);

    const handleDeleteClick = (key: string, name: string, isFolder: boolean) => {
        setObjectToDelete({ key, name, isFolder });
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = () => {
        if (objectToDelete) {
            onDeleteObject(objectToDelete.key);
            setObjectToDelete(null);
        }
    };

    return (
        <div className="">
            {/* Back Button */}
            <button
                onClick={onBack}
                className="text-sm text-gray-400 hover:text-white flex items-center gap-1"
            >
                ← Back to Buckets
            </button>

            {/* Bucket Header Card */}
            <div className="card p-0">
                <div className="p-4 border-b border-gray-700">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-primary-500/20 rounded-lg">
                                <CircleStackIcon className="w-6 h-6 text-primary-500" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-white">{bucket.name}</h3>
                                <div className="flex items-center gap-2 mt-1">
                                    <span
                                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${bucket.is_public
                                            ? 'bg-yellow-500/20 text-yellow-400'
                                            : 'bg-gray-700 text-gray-300'
                                            }`}
                                    >
                                        {bucket.is_public ? 'Public' : 'Private'}
                                    </span>
                                    <span className="text-sm text-gray-400">
                                        {objects.length} objects •{' '}
                                        {formatBytes(totalSizeBytes)}
                                    </span>
                                    <span className="text-sm text-gray-400">•</span>
                                    <span className="inline-flex items-center gap-1 text-sm font-medium text-green-400">
                                        <CurrencyRupeeIcon className="w-3.5 h-3.5" />
                                        {formatCurrency(bucket.total_billed_inr || 0)}
                                    </span>
                                </div>
                                {/* Public URL for public buckets */}
                                {bucket.is_public && (
                                    <div className="flex items-center gap-2 mt-2 p-2 bg-blue-500/10 rounded-lg">
                                        <GlobeAltIcon className="w-4 h-4 text-blue-400 flex-shrink-0" />
                                        <code className="text-xs text-blue-300 truncate flex-1">
                                            {getPublicBucketUrl()}
                                        </code>
                                        <button
                                            onClick={() => copyToClipboard(getPublicBucketUrl(), 'Public URL')}
                                            className="p-1 text-blue-400 hover:text-blue-300 hover:bg-blue-500/20 rounded"
                                            title="Copy public URL"
                                        >
                                            <ClipboardDocumentIcon className="w-4 h-4" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {activeTab === 'objects' && (
                                <button
                                    onClick={onUpload}
                                    className="btn-secondary flex items-center gap-2"
                                >
                                    <ArrowUpTrayIcon className="w-4 h-4" />
                                    Upload
                                </button>
                            )}
                            <button onClick={onDeleteBucket} className="btn-danger">
                                <TrashIcon className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="flex gap-4 mt-4 border-t border-gray-700 pt-4">
                        <button
                            onClick={() => setActiveTab('objects')}
                            className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === 'objects'
                                ? 'bg-primary-500/20 text-primary-400'
                                : 'text-gray-400 hover:text-white hover:bg-gray-800'
                                }`}
                        >
                            <FolderOpenIcon className="w-4 h-4" />
                            Objects
                        </button>
                        <button
                            onClick={() => setActiveTab('settings')}
                            className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === 'settings'
                                ? 'bg-primary-500/20 text-primary-400'
                                : 'text-gray-400 hover:text-white hover:bg-gray-800'
                                }`}
                        >
                            <Cog6ToothIcon className="w-4 h-4" />
                            Settings
                        </button>
                    </div>
                </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'objects' ? (
                <div className="card p-0">
                    {/* Path Navigation */}
                    {currentPath && (
                        <div className="p-3 border-b border-gray-700 bg-gray-800">
                            <div className="flex items-center gap-2 text-sm">
                                <button
                                    onClick={onNavigateUp}
                                    className="text-primary-500 hover:underline"
                                >
                                    ..
                                </button>
                                <span className="text-gray-400">/{currentPath}</span>
                            </div>
                        </div>
                    )}

                    {/* Objects List */}
                    <div className="overflow-x-auto">
                        {isLoading ? (
                            <div className="flex justify-center py-12">
                                <div className="w-8 h-8 border-4 border-gray-700 border-t-primary-500 rounded-full animate-spin"></div>
                            </div>
                        ) : objects.length > 0 ? (
                            <table className="w-full">
                                <thead className="bg-gray-800 border-b border-gray-700">
                                    <tr>
                                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase">
                                            Name
                                        </th>
                                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase">
                                            Size
                                        </th>
                                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase">
                                            Modified
                                        </th>
                                        <th className="text-right px-4 py-3 text-xs font-medium text-gray-400 uppercase">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-700">
                                    {objects.map((obj: ObjectInfo) => {
                                        const isFolder = obj.is_dir || obj.is_folder;
                                        const displayName =
                                            obj.key.split('/').filter(Boolean).pop() || obj.key;

                                        return (
                                            <tr key={obj.key} className="hover:bg-gray-800">
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-3">
                                                        {isFolder ? (
                                                            <FolderIcon className="w-5 h-5 text-yellow-500" />
                                                        ) : (
                                                            <DocumentIcon className="w-5 h-5 text-gray-400" />
                                                        )}
                                                        {isFolder ? (
                                                            <button
                                                                onClick={() =>
                                                                    onNavigateFolder(obj.key)
                                                                }
                                                                className="text-primary-500 hover:underline"
                                                            >
                                                                {displayName}
                                                            </button>
                                                        ) : (
                                                            <span className="text-white">
                                                                {displayName}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-400">
                                                    {isFolder ? '-' : formatBytes(obj.size || 0)}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-400">
                                                    {obj.last_modified
                                                        ? format(
                                                            new Date(obj.last_modified),
                                                            'MMM d, yyyy HH:mm'
                                                        )
                                                        : '-'}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center justify-end gap-1">
                                                        {/* Copy Public URL button for public buckets */}
                                                        {bucket.is_public && !isFolder && (
                                                            <button
                                                                onClick={() => copyToClipboard(getPublicObjectUrl(obj.key), 'Public URL')}
                                                                className="p-2 text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg"
                                                                title="Copy public URL"
                                                            >
                                                                <LinkIcon className="w-4 h-4" />
                                                            </button>
                                                        )}
                                                        {!isFolder && (
                                                            <button
                                                                onClick={() => onDownload(obj)}
                                                                className="p-2 text-gray-400 hover:text-primary-500 hover:bg-primary-500/10 rounded-lg"
                                                                title="Download"
                                                            >
                                                                <ArrowDownTrayIcon className="w-4 h-4" />
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={() => handleDeleteClick(obj.key, displayName, isFolder)}
                                                            className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg"
                                                            title="Delete"
                                                        >
                                                            <TrashIcon className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        ) : (
                            <div className="text-center py-12">
                                <FolderIcon className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                                <p className="text-gray-400">No objects in this bucket</p>
                                <button
                                    onClick={onUpload}
                                    className="mt-3 text-primary-500 hover:text-primary-400"
                                >
                                    Upload your first file
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <BucketSettingsPanel
                    bucket={bucket}
                    projectId={projectId}
                    onUpdate={onUpdateBucket}
                    onDelete={onDeleteBucket}
                    isUpdating={isUpdating}
                    updateError={updateError}
                />
            )}

            {/* Delete Object Modal */}
            <DeleteObjectModal
                isOpen={deleteModalOpen}
                onClose={() => {
                    setDeleteModalOpen(false);
                    setObjectToDelete(null);
                }}
                onConfirm={handleConfirmDelete}
                objectName={objectToDelete?.name || ''}
                isFolder={objectToDelete?.isFolder || false}
            />
        </div>
    );
};

export default BucketDetails;
